import { Input, Autocomplete, AutocompleteItem, Button } from '@heroui/react';
import { Copy, Trash2 } from 'lucide-react';
import type { MaterialLibrary, BanquetteAdditionalPanel } from '@estimator/shared';
import PanelFinishControls from '../shared/PanelFinishControls';
import PanelDimControls from '../shared/PanelDimControls';
import BoardRunControl from '../shared/BoardRunControl';

interface BanquetteAdditionalPanelRowProps {
  panel: BanquetteAdditionalPanel;
  materials: MaterialLibrary[];
  onChange: (updates: Partial<BanquetteAdditionalPanel>) => void;
  onRemove: () => void;
  /** Copy this row's material to the clipboard. Button hidden when absent. */
  onCopyMaterial?: () => void;
  /** Name of the template-level default profile, for the override popover label. */
  defaultFinishName?: string | null;
}

export default function BanquetteAdditionalPanelRow({ panel, materials, onChange, onRemove, onCopyMaterial, defaultFinishName }: BanquetteAdditionalPanelRowProps) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={panel.name}
        onValueChange={(v) => onChange({ name: v })}
        size="sm"
        placeholder="Panel name"
        className="flex-1"
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
      />
      <Input
        type="number"
        value={panel.width.toString()}
        onValueChange={(v) => onChange({ width: Number(v) || 0 })}
        size="sm"
        className="w-16"
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
        endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>w</span>}
      />
      <PanelDimControls
        link={panel.width_link ?? null}
        onChange={(width_link) => onChange({ width_link })}
        label="Width"
      />
      <Input
        type="number"
        value={panel.height.toString()}
        onValueChange={(v) => onChange({ height: Number(v) || 0 })}
        size="sm"
        className="w-16"
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
        endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>h</span>}
      />
      <PanelDimControls
        link={panel.height_link ?? null}
        onChange={(height_link) => onChange({ height_link })}
        label="Height"
      />
      <Input
        type="number"
        value={panel.qty_per_section.toString()}
        onValueChange={(v) => onChange({ qty_per_section: Math.max(1, Math.floor(Number(v) || 1)) })}
        size="sm"
        className="w-16"
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
        endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>/sec</span>}
      />
      <Autocomplete
        selectedKey={panel.material_id ?? undefined}
        onSelectionChange={(key) => onChange({ material_id: (key as string) || null })}
        size="sm"
        placeholder="Material"
        className="flex-1"
        inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
      >
        {materials.map((m) => (
          <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
        ))}
      </Autocomplete>
      <BoardRunControl
        widthIn={panel.width ?? 0}
        lengthIn={panel.height ?? 0}
        material={materials.find((m) => m.id === panel.material_id)}
        flipped={panel.board_run_flipped ?? false}
        onChange={(board_run_flipped) => onChange({ board_run_flipped })}
      />
      <PanelFinishControls
        finish={panel.finish}
        onChange={(updates) => onChange({ finish: { ...panel.finish, ...updates } })}
        defaultFinishName={defaultFinishName ?? null}
      />
      {onCopyMaterial && (
        <Button
          isIconOnly
          size="sm"
          variant="light"
          aria-label="Copy material"
          title="Copy this material"
          isDisabled={!panel.material_id}
          onPress={onCopyMaterial}
        >
          <Copy size={14} style={{ color: 'var(--text-muted)' }} />
        </Button>
      )}
      <Button isIconOnly size="sm" variant="light" onPress={onRemove}>
        <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
      </Button>
    </div>
  );
}
